import React, { useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Button from "@react/shared/ui/Button"
import Badge  from "@react/shared/ui/Badge"
import AcceptanceModal from "./AcceptanceModal"
import { dateFmt } from "../helpers/format"
import ActionButton from "./ActionButton"

const DECISION_TONE = {
  accepted:                "success",
  accepted_with_reserves:  "warning",
  refused:                 "danger",
}

const DECISION_ICON = {
  accepted:                "circle-check",
  accepted_with_reserves:  "triangle-exclamation",
  refused:                 "circle-xmark",
}

const FinalizedBlock = ({ data, onEdit }) => {
  const a = data.acceptance
  return (
    <div className="bg-emerald-50/60 border border-emerald-200 rounded-xl px-4 py-3 flex flex-wrap items-center gap-4">
      <div className="flex items-center gap-2 shrink-0">
        <span className="w-8 h-8 grid place-items-center rounded-full bg-emerald-100 text-emerald-700">
          <FontAwesomeIcon icon={DECISION_ICON[a.decision] || "stamp"} />
        </span>
        <div className="flex flex-col">
          <span className="text-[10.5px] uppercase tracking-wider font-medium text-emerald-700">
            {I18n.t("projects.acceptances.banner.eyebrow_finalized")}
          </span>
          <span className="text-[13px] font-semibold text-slate-900">{a.decision_label}</span>
        </div>
      </div>

      <div className="flex flex-col">
        <span className="text-[10.5px] uppercase tracking-wider font-medium text-emerald-700/80">
          {I18n.t("projects.acceptances.banner.acceptance_date")}
        </span>
        <span className="text-[13px] text-slate-900">{dateFmt(a.acceptance_date)}</span>
      </div>

      {a.reserves_snapshot_count > 0 && (
        <div className="flex flex-col">
          <span className="text-[10.5px] uppercase tracking-wider font-medium text-emerald-700/80">
            {I18n.t("projects.acceptances.banner.reserves_snapshot")}
          </span>
          <span className="text-[13px] text-slate-900 tabular-nums">{a.reserves_snapshot_count}</span>
        </div>
      )}

      {a.reserves_deadline && (
        <div className="flex flex-col">
          <span className="text-[10.5px] uppercase tracking-wider font-medium text-emerald-700/80">
            {I18n.t("projects.acceptances.banner.deadline")}
          </span>
          <span className="text-[13px] text-slate-900">{dateFmt(a.reserves_deadline)}</span>
        </div>
      )}

      <div className="ml-auto flex items-center gap-2">
        <Badge tone={DECISION_TONE[a.decision] || "neutral"} variant="soft" size="sm">
          <FontAwesomeIcon icon="lock" className="mr-1 w-2.5 h-2.5" />
          {I18n.t("projects.acceptances.banner.finalized")}
        </Badge>
        <Button variant="ghost" size="sm" onClick={onEdit}>
          <FontAwesomeIcon icon="eye" /> {I18n.t("projects.acceptances.banner.view")}
        </Button>
        {a.pdf_url && (
          <a
            href      = {a.pdf_url}
            target    = "_blank"
            rel       = "noopener noreferrer"
            className = "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-[12.5px] font-semibold shadow-sm"
          >
            <FontAwesomeIcon icon="file-pdf" /> {I18n.t("projects.acceptances.pdf.print")}
          </a>
        )}
      </div>
    </div>
  )
}

const DraftBlock = ({ data, canManage, onEdit }) => {
  const a = data.acceptance
  return (
    <div className="bg-amber-50/60 border border-amber-200 rounded-xl px-4 py-3 flex flex-wrap items-center gap-4">
      <div className="flex items-center gap-2 shrink-0">
        <span className="w-8 h-8 grid place-items-center rounded-full bg-amber-100 text-amber-700">
          <FontAwesomeIcon icon="file-pen" />
        </span>
        <div className="flex flex-col">
          <span className="text-[10.5px] uppercase tracking-wider font-medium text-amber-700">
            {I18n.t("projects.acceptances.banner.eyebrow_draft")}
          </span>
          <span className="text-[13px] font-semibold text-slate-900">{a.decision_label}</span>
        </div>
      </div>

      <div className="flex flex-col">
        <span className="text-[10.5px] uppercase tracking-wider font-medium text-amber-700/80">
          {I18n.t("projects.acceptances.banner.acceptance_date")}
        </span>
        <span className="text-[13px] text-slate-900">{dateFmt(a.acceptance_date)}</span>
      </div>

      <div className="ml-auto flex items-center gap-2">
        {a.pdf_url && (
          <a
            href      = {a.pdf_url}
            target    = "_blank"
            rel       = "noopener noreferrer"
            className = "inline-flex items-center gap-1.5 px-2.5 py-1 text-[12.5px] text-amber-700 hover:text-amber-900 hover:bg-amber-100 rounded-md"
          >
            <FontAwesomeIcon icon="file-pdf" /> {I18n.t("projects.acceptances.pdf.print")}
          </a>
        )}
        {canManage && (
          <ActionButton icon="pen" onClick={onEdit}>
            {I18n.t("projects.acceptances.banner.continue")}
          </ActionButton>
        )}
      </div>
    </div>
  )
}

const EmptyBlock = ({ data, canManage, onOpen }) => (
  <div className="bg-slate-50 border border-dashed border-slate-300 rounded-xl px-4 py-3 flex flex-wrap items-center gap-4">
    <div className="flex items-center gap-2">
      <span className="w-8 h-8 grid place-items-center rounded-full bg-slate-100 text-slate-700">
        <FontAwesomeIcon icon="stamp" />
      </span>
      <div className="flex flex-col">
        <span className="text-[13px] font-semibold text-slate-900">
          {I18n.t("projects.acceptances.banner.empty_title")}
        </span>
        <span className="text-[11.5px] text-slate-500">
          {data.live_reserves_count > 0
            ? I18n.t("projects.acceptances.banner.empty_hint_with_reserves", { count: data.live_reserves_count })
            : I18n.t("projects.acceptances.banner.empty_hint")}
        </span>
      </div>
    </div>
    {canManage && (
      <div className="ml-auto">
        <ActionButton icon="stamp" onClick={onOpen}>
          {I18n.t("projects.acceptances.banner.cta")}
        </ActionButton>
      </div>
    )}
  </div>
)

const AcceptanceBanner = ({ acceptance, policies }) => {
  const [modalOpen, setModalOpen] = useState(false)
  const [data, setData] = useState(acceptance)
  if (!data) return null
  const canManage = !!policies?.can_manage_acceptance
  const projectId = data.create_url?.match(/\/projects\/(\d+)\//)?.[1]

  const handleSaved = (next) => {
    if (next == null) {
      setData(d => ({ ...d, exists: false, acceptance: null }))
    } else {
      setData(d => ({ ...d, exists: true, acceptance: next }))
    }
  }

  const block = (() => {
    if (!data.exists) return <EmptyBlock data={data} canManage={canManage} onOpen={() => setModalOpen(true)} />
    if (data.acceptance?.finalized) return <FinalizedBlock data={data} onEdit={() => setModalOpen(true)} />
    return <DraftBlock data={data} canManage={canManage} onEdit={() => setModalOpen(true)} />
  })()

  return (
    <div className="px-7 mt-3">
      {block}
      <AcceptanceModal
        isOpen             = {modalOpen}
        projectId          = {projectId}
        acceptanceData     = {data}
        liveReservesCount  = {data.live_reserves_count}
        canManage          = {canManage}
        onClose            = {() => setModalOpen(false)}
        onSaved            = {handleSaved}
      />
    </div>
  )
}

export default AcceptanceBanner
